import { useEffect, useState } from 'react'
import { Form, Input, Button, Card, Typography, Alert } from 'antd'
import { UserOutlined, LockOutlined, MailOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useAuthStore, type OtpChallenge } from '../store/authStore'

const { Text } = Typography


// Знак Оркестратора: дуга уходит в круг с самолётиком - тот же рисунок, что
// был на экране входа, восстановлен по снимку. Верхняя надпись - CRM,
// под ней разрядкой название системы.
// Знак «CRM Оркестратор», вариант «Орбита»: дуга прочерчивается, по ней летит
// токен с самолётиком, следом проявляется вордмарк и название. Разметка и
// анимация перенесены один в один из макета логотипа.
const AnimatedLogo = () => (
    <svg
        className="orbit-logo"
        viewBox="0 0 380 190"
        role="img"
        aria-label="CRM Оркестратор"
        style={{ display: 'block', width: '100%', height: 'auto', overflow: 'visible', marginBottom: 18 }}
    >
        <defs>
            <linearGradient id="gArc" x1="0" y1="1" x2="1" y2="0">
                <stop className="st-brand-0" offset="0%" />
                <stop className="st-brand" offset="28%" />
                <stop className="st-brand2" offset="100%" />
            </linearGradient>
            <linearGradient id="gTok" x1="0" y1="0" x2="1" y2="1">
                <stop className="st-brand" offset="0%" />
                <stop className="st-brand2" offset="100%" />
            </linearGradient>
            <linearGradient id="gSheen" x1="0" y1="0" x2="1" y2="0">
                <stop className="st-white-0" offset="0%" />
                <stop className="st-white-75" offset="50%" />
                <stop className="st-white-0" offset="100%" />
            </linearGradient>
            <clipPath id="clipCRM">
                <text
                    x="190" y="132" textAnchor="middle" fontSize="86" fontWeight="800"
                    fontFamily="-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Helvetica,Arial,sans-serif"
                    letterSpacing="-2"
                >
                    CRM
                </text>
            </clipPath>
        </defs>

        {/* дуга-свуш */}
        <path className="swoosh" pathLength="100" d="M 20 122 C 14 56 96 20 194 22 C 258 24 306 36 338 54" />

        {/* вордмарк с бликом */}
        <g className="reveal">
            <text className="wordmark" x="190" y="132" textAnchor="middle" fontSize="86" letterSpacing="-2">CRM</text>
            <g clipPath="url(#clipCRM)">
                <rect className="sheen" x="-120" y="40" width="90" height="120" fill="url(#gSheen)" transform="skewX(-18)" />
            </g>
        </g>

        <text className="sub revealSub" x="190" y="164" textAnchor="middle" fontSize="15">ОРКЕСТРАТОР</text>

        {/* летящий токен */}
        <g className="token">
            <circle className="tokenPulse" r="20" />
            <circle r="20" fill="url(#gTok)" />
            <g transform="scale(0.62) translate(-12,-12)">
                <path d="M2.01 21L23 12L2.01 3L2 10L17 12L2 14L2.01 21Z" fill="#fff" />
            </g>
        </g>
    </svg>
)

export default function Login() {
    const [loading, setLoading] = useState(false)
    const navigate = useNavigate()
    const { login, verifyOtp, resendOtp, error, clearError } = useAuthStore()

    // Вход по коду: после пароля код ушёл на почту, ждём его.
    const [otp, setOtp] = useState<OtpChallenge | null>(null)
    const [otpUsername, setOtpUsername] = useState('')
    const [code, setCode] = useState('')
    const [resendLeft, setResendLeft] = useState(0)

    useEffect(() => {
        if (resendLeft <= 0) return
        const timer = window.setTimeout(() => setResendLeft((value) => value - 1), 1000)
        return () => window.clearTimeout(timer)
    }, [resendLeft])

    const onFinish = async (values: { username: string; password: string }) => {
        setLoading(true)
        clearError()
        try {
            const challenge = await login(values.username, values.password)
            if (challenge) {
                setOtp(challenge)
                setOtpUsername(values.username)
                setCode('')
                setResendLeft(challenge.resendIn)
                return
            }
            navigate('/')
        } catch {

        } finally {
            setLoading(false)
        }
    }

    const submitCode = async (value?: string) => {
        const entered = (value ?? code).replace(/\D/g, '')
        if (!otp || entered.length !== 6) return
        setLoading(true)
        clearError()
        try {
            await verifyOtp(otpUsername, otp.challengeId, entered)
            navigate('/')
        } catch {
            setCode('')
        } finally {
            setLoading(false)
        }
    }

    const sendAgain = async () => {
        if (!otp) return
        try {
            const challenge = await resendOtp(otp.challengeId)
            setOtp(challenge)
            setCode('')
            setResendLeft(challenge.resendIn)
        } catch {
            // Причина показана в общем сообщении об ошибке.
        }
    }

    const backToPassword = () => {
        setOtp(null)
        setCode('')
        clearError()
    }

    return (
        <>

            <style>{`
                /* --- Знак «CRM Оркестратор» ------------------------------
                   Цвета заведены локально: в макете это те же две пары
                   значений для светлой и тёмной темы. */
                .orbit-logo {
                    --brand: #0088cc;
                    --brand-2: #00b4d8;
                    --glow: rgba(0, 136, 204, 0.45);
                }

                [data-theme="dark"] .orbit-logo {
                    --brand: #29b6f6;
                    --brand-2: #64b5f6;
                    --glow: rgba(41, 182, 246, 0.4);
                }

                /* var() не работает в атрибутах SVG, поэтому цвета остановок
                   градиентов задаются классами. */
                .orbit-logo .st-brand-0 { stop-color: var(--brand); stop-opacity: 0; }
                .orbit-logo .st-brand { stop-color: var(--brand); }
                .orbit-logo .st-brand2 { stop-color: var(--brand-2); }
                .orbit-logo .st-white-0 { stop-color: #fff; stop-opacity: 0; }
                .orbit-logo .st-white-75 { stop-color: #fff; stop-opacity: .75; }

                .orbit-logo .wordmark {
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
                    font-weight: 800;
                    fill: var(--text-primary);
                }

                .orbit-logo .sub {
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
                    font-weight: 700;
                    fill: var(--brand);
                }

                .orbit-logo .swoosh {
                    fill: none;
                    stroke: url(#gArc);
                    stroke-width: 7;
                    stroke-linecap: round;
                    stroke-dasharray: 100;
                    stroke-dashoffset: 100;
                    animation: draw 6s cubic-bezier(.45, .05, .25, 1) infinite;
                }

                @keyframes draw {
                    0% { stroke-dashoffset: 100; }
                    58% { stroke-dashoffset: 0; }
                    92% { stroke-dashoffset: 0; opacity: 1; }
                    100% { stroke-dashoffset: 0; opacity: 0; }
                }

                .orbit-logo .token {
                    offset-path: path("M 20 122 C 14 56 96 20 194 22 C 258 24 306 36 338 54");
                    offset-rotate: 0deg;
                    filter: drop-shadow(0 6px 18px var(--glow));
                    animation: ride 6s cubic-bezier(.45, .05, .25, 1) infinite;
                }

                @keyframes ride {
                    0% { offset-distance: 0%; opacity: 0; scale: .4; }
                    8% { opacity: 1; scale: 1; }
                    58% { offset-distance: 100%; scale: 1; }
                    66% { scale: 1.12; }
                    74% { scale: 1; }
                    92% { offset-distance: 100%; opacity: 1; }
                    100% { offset-distance: 100%; opacity: 0; }
                }

                .orbit-logo .tokenPulse {
                    fill: none;
                    stroke: var(--brand);
                    stroke-width: 2;
                    transform-origin: center;
                    animation: tokenPulse 6s ease-in-out infinite;
                }

                @keyframes tokenPulse {
                    0%, 58% { opacity: 0; r: 20; }
                    68% { opacity: .5; r: 34; }
                    80%, 100% { opacity: 0; r: 40; }
                }

                .orbit-logo .reveal {
                    animation: reveal 6s cubic-bezier(.2, .8, .2, 1) infinite;
                    transform-origin: center;
                }

                @keyframes reveal {
                    0%, 14% { opacity: 0; transform: translateY(16px); }
                    36%, 92% { opacity: 1; transform: translateY(0); }
                    100% { opacity: 0; transform: translateY(-8px); }
                }

                .orbit-logo .revealSub {
                    animation: revealSub 6s cubic-bezier(.2, .8, .2, 1) infinite;
                    letter-spacing: 8px;
                }

                @keyframes revealSub {
                    0%, 26% { opacity: 0; letter-spacing: 2px; }
                    46%, 92% { opacity: 1; letter-spacing: 8px; }
                    100% { opacity: 0; }
                }

                .orbit-logo .sheen {
                    animation: sheen 6s ease-in-out infinite;
                }

                @keyframes sheen {
                    0%, 44% { transform: translateX(-260px); }
                    62%, 100% { transform: translateX(420px); }
                }

                /* Если в системе просят меньше движения - показываем знак
                   собранным, без прокрутки анимации. */
                @media (prefers-reduced-motion: reduce) {
                    .orbit-logo .swoosh,
                    .orbit-logo .token,
                    .orbit-logo .tokenPulse,
                    .orbit-logo .reveal,
                    .orbit-logo .revealSub,
                    .orbit-logo .sheen {
                        animation: none !important;
                        opacity: 1 !important;
                        stroke-dashoffset: 0 !important;
                        offset-distance: 100% !important;
                    }
                }
                @keyframes rotateText {
                    0% { transform: rotateY(0deg); }
                    100% { transform: rotateY(360deg); }
                }

                @keyframes iconPulse {
                    0%, 100% {
                        transform: scale(1);
                        box-shadow: 0 12px 48px rgba(0, 136, 204, 0.4);
                    }
                    50% {
                        transform: scale(1.02);
                        box-shadow: 0 16px 60px rgba(0, 136, 204, 0.55);
                    }
                }

                @keyframes bgShift {
                    0%, 100% { background-position: 0% 50%; }
                    50% { background-position: 100% 50%; }
                }

                .login-page {
                    min-height: 100vh;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: var(--bg-base);
                    padding: 20px;
                    overflow: hidden;
                    position: relative;
                }

                .login-page::before {
                    content: '';
                    position: absolute;
                    top: -50%;
                    left: -50%;
                    width: 200%;
                    height: 200%;
                    background: radial-gradient(circle at 30% 30%, rgba(0, 136, 204, 0.08) 0%, transparent 40%),
                                radial-gradient(circle at 70% 70%, rgba(0, 180, 216, 0.05) 0%, transparent 40%);
                    animation: bgShift 15s ease-in-out infinite;
                    pointer-events: none;
                }

                .login-card {
                    position: relative;
                    z-index: 1;
                    width: 100%;
                    max-width: 420px;
                    background: var(--bg-surface) !important;
                    backdrop-filter: blur(20px);
                    border: 1px solid var(--border) !important;
                    border-radius: 24px !important;
                    box-shadow: var(--shadow-xl) !important;
                }

                .login-input {
                    background: var(--bg-subtle) !important;
                    border-color: var(--border) !important;
                    border-radius: 12px !important;
                    height: 52px !important;
                    transition: all 0.3s ease !important;
                }

                .login-input:hover,
                .login-input:focus {
                    border-color: var(--telegram) !important;
                    box-shadow: 0 0 0 2px rgba(0, 136, 204, 0.1) !important;
                }

                .login-input input {
                    background: transparent !important;
                    color: var(--text-primary) !important;
                }

                .login-input .ant-input-prefix {
                    color: var(--text-muted) !important;
                }

                .login-btn {
                    height: 52px !important;
                    border-radius: 12px !important;
                    font-size: 16px !important;
                    font-weight: 600 !important;
                    background: var(--brand-primary) !important;
                    border: none !important;
                    box-shadow: 0 8px 32px rgba(0, 136, 204, 0.2) !important;
                    transition: all 0.3s ease !important;
                }

                .login-btn:hover {
                    background: var(--brand-primary-hover) !important;
                    transform: translateY(-2px) !important;
                    box-shadow: 0 12px 40px rgba(0, 136, 204, 0.3) !important;
                }

                .login-btn:active {
                    transform: translateY(0) !important;
                }
            `}</style>

            <div className="login-page">
                <Card
                    className="login-card"
                    styles={{ body: { padding: 48 } }}
                >
                    <AnimatedLogo />

                    {error && (
                        <Alert
                            message={error}
                            type="error"
                            showIcon
                            style={{
                                marginBottom: 24,
                                background: 'rgba(255, 77, 79, 0.1)',
                                border: '1px solid rgba(255, 77, 79, 0.3)',
                                borderRadius: 12,
                            }}
                            closable
                            onClose={clearError}
                        />
                    )}

                    {otp ? (
                        <div>
                            <div style={{ textAlign: 'center', marginBottom: 20 }}>
                                <MailOutlined style={{ fontSize: 28, color: 'var(--text-secondary)' }} />
                                <div style={{ marginTop: 10, fontWeight: 600 }}>Введите код из письма</div>
                                <Text type="secondary" style={{ fontSize: 13 }}>
                                    Код отправлен на {otp.sentTo || 'вашу личную почту'}. Он действует 10 минут.
                                </Text>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
                                <Input.OTP
                                    length={6}
                                    size="large"
                                    value={code}
                                    autoFocus
                                    onChange={(value) => { setCode(value); void submitCode(value) }}
                                    formatter={(value) => value.replace(/\D/g, '')}
                                />
                            </div>
                            <Button
                                type="primary"
                                block
                                size="large"
                                loading={loading}
                                disabled={code.replace(/\D/g, '').length !== 6}
                                onClick={() => void submitCode()}
                                className="login-btn"
                            >
                                Войти
                            </Button>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
                                <Button type="link" style={{ padding: 0 }} onClick={backToPassword}>
                                    Назад
                                </Button>
                                <Button type="link" style={{ padding: 0 }} disabled={resendLeft > 0} onClick={() => void sendAgain()}>
                                    {resendLeft > 0 ? `Отправить ещё раз через ${resendLeft} с` : 'Отправить код ещё раз'}
                                </Button>
                            </div>
                        </div>
                    ) : (
                    <Form
                        name="login"
                        onFinish={onFinish}
                        autoComplete="off"
                        layout="vertical"
                        size="large"
                    >
                        <Form.Item
                            name="username"
                            rules={[{ required: true, message: 'Введите логин' }]}
                        >
                            <Input
                                prefix={<UserOutlined />}
                                placeholder="Логин"
                                className="login-input"
                            />
                        </Form.Item>

                        <Form.Item
                            name="password"
                            rules={[{ required: true, message: 'Введите пароль' }]}
                        >
                            <Input.Password
                                prefix={<LockOutlined />}
                                placeholder="Пароль"
                                className="login-input"
                            />
                        </Form.Item>

                        <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                                block
                                className="login-btn"
                            >
                                Войти
                            </Button>
                        </Form.Item>
                    </Form>
                    )}

                    <div style={{ marginTop: 32, textAlign: 'center' }}>
                        <Text style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                            Оркестратор v1.0
                        </Text>
                    </div>
                </Card>
            </div>
        </>
    )
}
